/**
 * Design Tokens System для Event Master
 * Основа всей дизайн-системы с типизацией TypeScript
 */

// ===== COLOR TOKENS =====
export const colorTokens = {
  // Primary Colors - основной бренд
  primary: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9", // Main primary
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49",
  },

  // Secondary Colors - акценты
  secondary: {
    50: "#fdf4ff",
    100: "#fae8ff",
    200: "#f5d0fe",
    300: "#f0abfc",
    400: "#e879f9",
    500: "#d946ef", // Main secondary
    600: "#c026d3",
    700: "#a21caf",
    800: "#86198f",
    900: "#701a75",
    950: "#4a044e",
  },

  // Success Colors
  success: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e", // Main success
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16",
  },

  // Warning Colors
  warning: {
    50: "#fefdf2",
    100: "#fef7cd",
    200: "#feee95",
    300: "#fde047",
    400: "#facc15",
    500: "#eab308", // Main warning
    600: "#ca8a04",
    700: "#a16207",
    800: "#854d0e",
    900: "#713f12",
    950: "#422006",
  },

  // Error Colors
  error: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444", // Main error
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
  },

  // Info Colors
  info: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9", // Main info (same as primary)
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49",
  },

  // Neutral/Gray Colors
  gray: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
    950: "#020617",
  },

  // Background Colors
  background: {
    default: "#fafaf9",
    paper: "#ffffff",
    surface: "#f8fafc",
    overlay: "rgba(0, 0, 0, 0.5)",
  },

  // Text Colors
  text: {
    primary: "#0f172a",
    secondary: "#475569",
    disabled: "#94a3b8",
    inverse: "#ffffff",
  },

  // Border Colors
  border: {
    light: "#e2e8f0",
    main: "#cbd5e1",
    strong: "#94a3b8",
    focus: "#0ea5e9",
  },
} as const;

// ===== TYPOGRAPHY TOKENS =====
export const typographyTokens = {
  // Font Families
  fontFamily: {
    primary: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "sans-serif",
    ],
    secondary: [
      "Roboto",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "sans-serif",
    ],
    mono: [
      "JetBrains Mono",
      "SF Mono",
      "Monaco",
      "Inconsolata",
      "Fira Code",
      "Dank Mono",
      "Consolas",
      "monospace",
    ],
  },

  // Font Sizes (в rem для масштабируемости)
  fontSize: {
    xs: "0.75rem", // 12px
    sm: "0.875rem", // 14px
    base: "1rem", // 16px
    lg: "1.125rem", // 18px
    xl: "1.25rem", // 20px
    "2xl": "1.5rem", // 24px
    "3xl": "1.875rem", // 30px
    "4xl": "2.25rem", // 36px
    "5xl": "3rem", // 48px
    "6xl": "3.75rem", // 60px
    "7xl": "4.5rem", // 72px
  },

  // Font Weights
  fontWeight: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },

  // Line Heights
  lineHeight: {
    none: "1",
    tight: "1.25",
    snug: "1.375",
    normal: "1.5",
    relaxed: "1.625",
    loose: "2",
  },

  // Letter Spacing
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em",
  },
} as const;

// ===== SPACING TOKENS =====
export const spacingTokens = {
  0: "0px",
  1: "0.25rem", // 4px
  2: "0.5rem", // 8px
  3: "0.75rem", // 12px
  4: "1rem", // 16px
  5: "1.25rem", // 20px
  6: "1.5rem", // 24px
  7: "1.75rem", // 28px
  8: "2rem", // 32px
  9: "2.25rem", // 36px
  10: "2.5rem", // 40px
  11: "2.75rem", // 44px
  12: "3rem", // 48px
  14: "3.5rem", // 56px
  16: "4rem", // 64px
  20: "5rem", // 80px
  24: "6rem", // 96px
  28: "7rem", // 112px
  32: "8rem", // 128px
  36: "9rem", // 144px
  40: "10rem", // 160px
  44: "11rem", // 176px
  48: "12rem", // 192px
  52: "13rem", // 208px
  56: "14rem", // 224px
  60: "15rem", // 240px
  64: "16rem", // 256px
  72: "18rem", // 288px
  80: "20rem", // 320px
  96: "24rem", // 384px
} as const;

// ===== BORDER RADIUS TOKENS =====
export const borderRadiusTokens = {
  none: "0px",
  sm: "0.125rem", // 2px
  base: "0.25rem", // 4px
  md: "0.375rem", // 6px
  lg: "0.5rem", // 8px
  xl: "0.75rem", // 12px
  "2xl": "1rem", // 16px
  "3xl": "1.5rem", // 24px
  full: "9999px",
} as const;

// ===== SHADOW TOKENS =====
export const shadowTokens = {
  none: "none",
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  base: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
} as const;

// ===== BREAKPOINT TOKENS =====
export const breakpointTokens = {
  xs: "320px", // Mobile small
  sm: "768px", // Mobile large / Tablet small
  md: "1024px", // Tablet large / Desktop small
  lg: "1440px", // Desktop large
  xl: "1920px", // Desktop extra large
} as const;

// ===== Z-INDEX TOKENS =====
export const zIndexTokens = {
  hide: -1,
  auto: "auto",
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// ===== TRANSITION TOKENS =====
export const transitionTokens = {
  duration: {
    fastest: "100ms",
    fast: "200ms",
    normal: "300ms",
    slow: "500ms",
    slowest: "750ms",
  },
  easing: {
    linear: "linear",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
  },
} as const;

// ===== COMPONENT SPECIFIC TOKENS =====
export const componentTokens = {
  button: {
    height: {
      sm: "2rem", // 32px
      md: "2.5rem", // 40px
      lg: "3rem", // 48px
    },
    padding: {
      sm: "0.5rem 0.75rem", // 8px 12px
      md: "0.75rem 1rem", // 12px 16px
      lg: "1rem 1.5rem", // 16px 24px
    },
    borderRadius: {
      sm: "0.25rem", // 4px
      md: "0.375rem", // 6px
      lg: "0.5rem", // 8px
    },
  },
  input: {
    height: {
      sm: "2rem", // 32px
      md: "2.5rem", // 40px
      lg: "3rem", // 48px
    },
    padding: "0.75rem", // 12px
    borderRadius: "0.375rem", // 6px
  },
  card: {
    padding: {
      sm: "1rem", // 16px
      md: "1.5rem", // 24px
      lg: "2rem", // 32px
    },
    borderRadius: "0.5rem", // 8px
  },
  modal: {
    borderRadius: "0.75rem", // 12px
    maxWidth: {
      sm: "24rem", // 384px
      md: "32rem", // 512px
      lg: "48rem", // 768px
      xl: "64rem", // 1024px
    },
  },
} as const;

// ===== ЭКСПОРТ ВСЕХ ТОКЕНОВ =====
export const designTokens = {
  colors: colorTokens,
  typography: typographyTokens,
  spacing: spacingTokens,
  borderRadius: borderRadiusTokens,
  shadows: shadowTokens,
  breakpoints: breakpointTokens,
  zIndex: zIndexTokens,
  transitions: transitionTokens,
  components: componentTokens,
} as const;

// ===== ТИПЫ ДЛЯ TYPESCRIPT =====
export type ColorTokens = typeof colorTokens;
export type TypographyTokens = typeof typographyTokens;
export type SpacingTokens = typeof spacingTokens;
export type BorderRadiusTokens = typeof borderRadiusTokens;
export type ShadowTokens = typeof shadowTokens;
export type BreakpointTokens = typeof breakpointTokens;
export type ZIndexTokens = typeof zIndexTokens;
export type TransitionTokens = typeof transitionTokens;
export type ComponentTokens = typeof componentTokens;
export type DesignTokens = typeof designTokens;

export default designTokens;
